/**
 * Search Query Hook
 *
 * TanStack Query-powered search hook with debouncing and caching.
 *
 * `mode` picks the server's arms (`services/search/search.service.ts`):
 * `hybrid` (the default) merges keyword and meaning matches in one response;
 * the palette asks `keyword` and `semantic` separately instead, so ranked
 * keyword rows paint without waiting on the embedding call.
 *
 * - keepPreviousData prevents flash between keystrokes
 * - staleTime avoids redundant refetches for repeated queries
 * - Automatic invalidation via WebSocket entity change events
 */

import { useMemo } from 'react';
import { useQuery, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import {
  searchService,
  type EntityType,
  type SearchResult,
  type SearchPage,
  type SearchMode,
} from '../../services/search.service';
import { ApiResponseError } from '../../services/api/http-client.types';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useDebounce } from '../ui/useDebounce';

/** Sorted entityTypes ensure cache dedup regardless of array order */
export const searchQueryKey = (workspaceId: string, query: string, entityTypes?: EntityType[]) =>
  ['search', { workspaceId, query, entityTypes: entityTypes?.slice().sort() }] as const;

/**
 * The app-wide single retry, narrowed to the failures nothing else re-asks.
 *
 * - Network errors and 5xx: `apiService` has already retried them once with
 *   a backoff, so another round here would make four requests and hold the
 *   error back for 3s+.
 * - 429: the server is asking for fewer requests. Re-asking spends more of
 *   the same budget.
 *
 * What is left is a 4xx, which keeps the retry the app-wide default exists
 * for: the quiet pre-auth 401 (see `lib/queryClient.ts`).
 */
export function shouldRetrySearch(failureCount: number, error: Error): boolean {
  const status = error instanceof ApiResponseError ? error.status : undefined;
  return failureCount < 1 && status !== undefined && status < 500 && status !== 429;
}

interface UseSearchQueryOptions {
  entityTypes?: EntityType[];
  limit?: number;
  enabled?: boolean;
  mode?: SearchMode;
  /** Wait for a longer pause than keyword search does (the meaning request). */
  debounceMs?: number;
  /** Ask only once the DEBOUNCED query has this many characters (default 1). */
  minLength?: number;
  /** Fold a Live run's captures into one row (see `SearchOptions.groupGenerated`). */
  groupGenerated?: boolean;
}

/**
 * 200 ms, Algolia's measured recommendation for search-as-you-type: long enough
 * to skip the keystrokes inside a word, short enough that results track typing
 * (over 300 ms reads as lag). A superseded request is cancelled, not awaited.
 */
const SEARCH_DEBOUNCE_MS = 200;

export function useSearchQuery(query: string, options?: UseSearchQueryOptions) {
  const workspaceId = useWorkspaceId();
  const debouncedQuery = useDebounce(query, options?.debounceMs ?? SEARCH_DEBOUNCE_MS);
  const mode = options?.mode ?? 'hybrid';
  const limit = options?.limit ?? 20;

  const searchResult = useQuery({
    queryKey: [
      ...searchQueryKey(workspaceId || '', debouncedQuery, options?.entityTypes),
      mode,
      limit,
      options?.groupGenerated ?? false,
    ],
    queryFn: ({ signal }) =>
      searchService.searchPaginated({
        query: debouncedQuery,
        entityTypes: options?.entityTypes,
        limit,
        sortBy: 'mixed',
        mode,
        groupGenerated: options?.groupGenerated,
        signal,
      }),
    // Gated on the same debounced snapshot the key is built from, so the gate
    // and the request can never disagree about which query they mean.
    enabled:
      !!workspaceId &&
      debouncedQuery.trim().length >= (options?.minLength ?? 1) &&
      options?.enabled !== false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: shouldRetrySearch,
  });

  const results = useMemo<SearchResult[]>(
    () => searchResult.data?.results ?? [],
    [searchResult.data],
  );

  return {
    results,
    total: searchResult.data?.total ?? 0,
    /** True while `results` are an earlier query's answer, kept to avoid a flash. */
    isPlaceholderData: searchResult.isPlaceholderData,
    isLoading: searchResult.isLoading,
    isFetching: searchResult.isFetching,
    error: searchResult.error,
    refetch: searchResult.refetch,
    debouncedQuery,
  };
}

/** Query key for infinite search (distinct from fixed-limit search) */
const infiniteSearchQueryKey = (workspaceId: string, query: string, entityTypes?: EntityType[]) =>
  ['search', 'infinite', { workspaceId, query, entityTypes: entityTypes?.slice().sort() }] as const;

const DEFAULT_PAGE_SIZE = 20;

interface UseInfiniteSearchQueryOptions {
  entityTypes?: EntityType[];
  pageSize?: number;
  enabled?: boolean;
  /** Fold a Live run's captures into one row (see `SearchOptions.groupGenerated`). */
  groupGenerated?: boolean;
}

/**
 * Infinite search hook — offset-paginated with `useInfiniteQuery`.
 *
 * - Each page is ranked keyword search: an append-only page of one ranked
 *   list, so a later page never reorders the rows above it
 * - First page fires on debounced query change
 * - Subsequent pages fetched via `fetchNextPage` (driven by IntersectionObserver)
 */
export function useInfiniteSearchQuery(query: string, options?: UseInfiniteSearchQueryOptions) {
  const workspaceId = useWorkspaceId();
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;

  const infiniteResult = useInfiniteQuery<SearchPage>({
    queryKey: [
      ...infiniteSearchQueryKey(workspaceId || '', debouncedQuery, options?.entityTypes),
      options?.groupGenerated ?? false,
    ],
    queryFn: ({ pageParam = 0, signal }) =>
      searchService.searchPaginated({
        query: debouncedQuery,
        entityTypes: options?.entityTypes,
        limit: pageSize,
        offset: pageParam as number,
        sortBy: 'mixed',
        mode: 'keyword',
        groupGenerated: options?.groupGenerated,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    enabled: !!workspaceId && debouncedQuery.length > 0 && options?.enabled !== false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: shouldRetrySearch,
  });

  const results = useMemo<SearchResult[]>(
    () => infiniteResult.data?.pages.flatMap((page) => page.results) ?? [],
    [infiniteResult.data],
  );

  const total = infiniteResult.data?.pages[infiniteResult.data.pages.length - 1]?.total ?? 0;

  return {
    results,
    total,
    isLoading: infiniteResult.isLoading,
    isFetching: infiniteResult.isFetching,
    isFetchingNextPage: infiniteResult.isFetchingNextPage,
    hasNextPage: infiniteResult.hasNextPage,
    fetchNextPage: infiniteResult.fetchNextPage,
    isFetchNextPageError: infiniteResult.isFetchNextPageError,
    error: infiniteResult.error,
    refetch: infiniteResult.refetch,
    debouncedQuery,
  };
}

export type { SearchResult, EntityType };
